import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle, Smartphone, ListPlus, Settings2, AlertCircle, FileText, Target, Users } from 'lucide-react';
import { apiClient } from '../utils/api';
import { notifyTestSubmission } from '../utils/telegram';
import './CreateTestPage.css';

const PLANS = [
  { id: 'essential', name: 'Essential', price: 1999, testers: 20, iterations: 1, desc: 'Perfect for quick sanity checks' },
  { id: 'pro', name: 'Pro', price: 4999, testers: 20, iterations: 2, desc: 'Recommended for major releases', popular: true },
  { id: 'enterprise', name: 'Enterprise', price: 9999, testers: 40, iterations: 2, desc: 'Comprehensive coverage across devices' },
];

export default function CreateTestPage({ company, onClose, showToast }) {
  const [form, setForm] = useState({
    app_name: '',
    app_package: '',
    test_instructions: '',
    play_store_url: '',
    web_app_url: '',
  });

  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentMode, setPaymentMode] = useState(null); // 'now' | 'later'

  const [criteria, setCriteria] = useState({
    device_tier: '',
    network_type: '',
    max_ram_gb: '',
    allowed_states: '',
    allowed_cities: '',
  });

  const [apkFile, setApkFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  const update = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };
  const updateCriteria = (key, value) => setCriteria(prev => ({ ...prev, [key]: value }));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.apk')) {
      setApkFile(file);
    } else {
      showToast('Please upload an APK file', 'error');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) setApkFile(file);
  };

  const handleSubmit = async (mode) => {
    if (!form.app_name.trim()) { showToast('App name is required', 'error'); return; }
    if (!form.test_instructions.trim()) { showToast('Test instructions are required', 'error'); return; }
    if (!hasAppSource) { showToast('Please provide an APK, Play Store URL, or Web App link', 'error'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('company_id', company.id);
      formData.append('company_name', company.company_name || company.name);
      formData.append('app_name', form.app_name.trim());
      formData.append('app_package', form.app_package.trim());
      formData.append('instructions', form.test_instructions.trim());
      formData.append('play_store_url', form.play_store_url.trim());
      formData.append('web_app_url', form.web_app_url.trim());
      formData.append('priority', 'normal');
      const plan = PLANS.find(p => p.id === selectedPlan);
      formData.append('tester_quota', plan.testers);
      formData.append('testing_iterations', plan.iterations);
      if (apkFile) formData.append('apk', apkFile);

      const res = await apiClient.createTest(formData);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast('Failed to create test: ' + (data.error || 'Unknown error'), 'error');
        return;
      }

      const created = await res.json();

      const hasAnyCriteria = Object.values(criteria).some(v => v !== '');
      if (hasAnyCriteria && created.id) {
        const criteriaPayload = {};
        if (criteria.device_tier) criteriaPayload.device_tier = criteria.device_tier;
        if (criteria.network_type) criteriaPayload.network_type = criteria.network_type;
        if (criteria.max_ram_gb) criteriaPayload.max_ram_gb = Number(criteria.max_ram_gb);
        if (criteria.allowed_states.trim()) criteriaPayload.allowed_states = criteria.allowed_states.trim();
        if (criteria.allowed_cities.trim()) criteriaPayload.allowed_cities = criteria.allowed_cities.trim();
        try { await apiClient.setTestCriteria(created.id, criteriaPayload); } catch (e) { console.warn(e); }
      }

      notifyTestSubmission(company, form, plan.name, plan.price);
      setPaymentMode(mode);
      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      showToast('Failed to create test: ' + err.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handlePayNow = () => {
    const plan = PLANS.find(p => p.id === selectedPlan);
    // TODO: Integrate real Razorpay here
    showToast(`Razorpay checkout coming soon — ₹${plan.price.toLocaleString()}`, 'info');
  };

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const hasCriteria = Object.values(criteria).some(v => v !== '');
  const hasAppSource = apkFile !== null || form.play_store_url.trim() !== '' || form.web_app_url.trim() !== '';

  return (
    <div className="create-test-page">
      {uploading && (
        <div className="upload-overlay">
          <div className="upload-overlay-card">
            <div className="upload-overlay-spinner-ring" />
            <h2 className="upload-overlay-title">Uploading Project...</h2>
            <p className="upload-overlay-sub">Please do not close this page while we process your application build.</p>
            <div className="upload-overlay-dots">
              <span /><span /><span />
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="payment-modal-overlay">
          <div className="payment-modal-content">
            <CheckCircle className="payment-success-icon" size={48} />
            {paymentMode === 'later' ? (
              <>
                <h2>Test is Live — Results Coming In</h2>
                <p>
                  Your test session is now active. Testers are being matched and results will start appearing on your dashboard shortly.
                </p>
                <div className="payment-modal-preview-note">
                  <span className="lock-icon">&#128274;</span>
                  <p>
                    You can see how many issues were found for free. To unlock <strong>full recordings, AI analysis, and tester details</strong>, pay ₹{PLANS.find(p => p.id === selectedPlan).price.toLocaleString()} when you're ready.
                  </p>
                </div>
                <button className="payment-modal-btn" style={{ marginBottom: '10px' }} onClick={handlePayNow}>
                  Pay Now with Razorpay — ₹{PLANS.find(p => p.id === selectedPlan).price.toLocaleString()}
                </button>
                <button className="payment-modal-btn-ghost" onClick={onClose}>
                  View Dashboard (Free Preview)
                </button>
              </>
            ) : (
              <>
                <h2>Session Pending Approval</h2>
                <p>
                  Your test session is officially under review. You will receive an email shortly with a UPI QR code to complete the payment of <strong>₹{PLANS.find(p => p.id === selectedPlan).price.toLocaleString()}</strong>.
                </p>
                <p className="payment-modal-sub">
                  Once payment is confirmed, your session will be approved and distributed to our network of testers.
                </p>
                <button className="payment-modal-btn" onClick={onClose}>
                  Return to Dashboard
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="ct-bare-header">
        <h1>New test session</h1>
        <button className="ct-cancel-ghost" onClick={onClose}>
          <span>×</span> Cancel
        </button>
      </div>

      <div className="create-test-grid">
        {/* Left Column: Form Fields (62%) */}
        <div className="create-test-main-col">
          <section className="ct-form-section">
            <h2 className="ct-quiet-heading">Basic Configuration</h2>

            <div className="form-group">
              <label>App Name</label>
              <input
                type="text"
                className="ct-minimal-input"
                value={form.app_name}
                onChange={e => update('app_name', e.target.value)}
                placeholder="e.g., My Shopping App"
              />
            </div>

            <div className="form-group">
              <label>Package Name <span className="qualifier">(optional)</span></label>
              <input
                type="text"
                className="ct-minimal-input"
                value={form.app_package}
                onChange={e => update('app_package', e.target.value)}
                placeholder="e.g., com.example.myapp"
              />
            </div>

            <div className="form-group">
              <label>Test Instructions</label>
              <textarea
                className="ct-minimal-textarea"
                value={form.test_instructions}
                onChange={e => update('test_instructions', e.target.value)}
                placeholder={"1. Open the app and sign up with a new account\n2. Browse the home screen\n3. Add any item to cart\n4. Try to checkout\n5. Report anything that looks wrong or crashes"}
              />
              <span className="minimal-hint">
                Be specific — testers follow these step by step.
              </span>
            </div>


          </section>

          <section className="ct-form-section">
            <h2 className="ct-quiet-heading">Choose Your Plan</h2>
            <div className="ct-plans-grid">
              {PLANS.map(plan => (
                <div
                  key={plan.id}
                  className={`ct-plan-card ${selectedPlan === plan.id ? 'selected' : ''} ${plan.popular ? 'popular' : ''}`}
                  onClick={() => setSelectedPlan(plan.id)}
                >
                  {plan.popular && <span className="ct-plan-badge">Recommended</span>}
                  <div className="ct-plan-header">
                    <h3>{plan.name}</h3>
                    <div className="ct-plan-price">₹{plan.price.toLocaleString()}</div>
                  </div>
                  <p className="ct-plan-desc">{plan.desc}</p>
                  <ul className="ct-plan-features">
                    <li><Users size={14} /> {plan.testers} Testers</li>
                    <li><ListPlus size={14} /> {plan.iterations} Test Iterations</li>
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section className="ct-form-section">
            <h2 className="ct-quiet-heading">Tester Targeting</h2>
            <p className="ct-quiet-subtitle">Leave all blank to accept testers on any device.</p>

            <div className="form-row">
              <div className="form-group flex-1">
                <label>Device Tier</label>
                <select
                  className="ct-minimal-select"
                  value={criteria.device_tier}
                  onChange={e => updateCriteria('device_tier', e.target.value)}
                >
                  <option value="">Any device</option>
                  <option value="low">Low-end (budget)</option>
                  <option value="mid">Mid-range</option>
                  <option value="high">Flagship</option>
                </select>
              </div>
              <div className="form-group flex-1">
                <label>Network Type</label>
                <select
                  className="ct-minimal-select"
                  value={criteria.network_type}
                  onChange={e => updateCriteria('network_type', e.target.value)}
                >
                  <option value="">Any network</option>
                  <option value="2g">2G (very slow)</option>
                  <option value="3g">3G (slow)</option>
                  <option value="4g">4G / LTE</option>
                  <option value="5g">5G</option>
                  <option value="wifi">WiFi only</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Max RAM (GB) <span className="qualifier">(optional)</span></label>
              <input
                type="number"
                min="1"
                className="ct-minimal-input"
                value={criteria.max_ram_gb}
                onChange={e => updateCriteria('max_ram_gb', e.target.value)}
                placeholder="e.g., 4 — limits to ≤ 4 GB RAM"
              />
            </div>

            <div className="form-row">
              <div className="form-group flex-1">
                <label>States <span className="qualifier">(comma-separated)</span></label>
                <input
                  type="text"
                  className="ct-minimal-input"
                  value={criteria.allowed_states}
                  onChange={e => updateCriteria('allowed_states', e.target.value)}
                  placeholder="e.g., Maharashtra, Bihar"
                />
              </div>
              <div className="form-group flex-1">
                <label>Cities <span className="qualifier">(comma-separated)</span></label>
                <input
                  type="text"
                  className="ct-minimal-input"
                  value={criteria.allowed_cities}
                  onChange={e => updateCriteria('allowed_cities', e.target.value)}
                  placeholder="e.g., Mumbai, Patna"
                />
              </div>
            </div>
          </section>
        </div>

        {/* Right Column: Upload & Checklist (35%) */}
        <div className="create-test-sidebar-col">
          <div className="ct-sticky-box">
            <h2 className="ct-quiet-heading">Application build</h2>
            <p className="ct-quiet-subtitle" style={{ marginBottom: '12px' }}>Provide at least one of the following.</p>

            {/* Option 1: APK */}
            <div
              className={`minimal-apk-dropzone ${dragActive ? 'active' : ''} ${apkFile ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => !apkFile && fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".apk"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {apkFile ? (
                <div className="minimal-apk-selected">
                  <div className="apk-main-info">
                    <span className="apk-name">{apkFile.name}</span>
                    <span className="apk-size">{formatSize(apkFile.size)}</span>
                  </div>
                  <button
                    className="apk-remove-btn"
                    onClick={(e) => { e.stopPropagation(); setApkFile(null); }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="minimal-apk-placeholder">
                  <Upload size={24} className="upload-icon" />
                  <span className="upload-title">Upload APK file</span>
                  <span className="upload-sub">Drag & drop or click to browse</span>
                  <span className="upload-meta">Max 500 MB · .apk files only</span>
                </div>
              )}
            </div>

            {/* OR Divider */}
            <div className="ct-or-divider"><span>OR</span></div>

            {/* Option 2: Play Store URL */}
            <div className="form-group" style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '11px', fontWeight: 500, color: '#9ca3af', marginBottom: '6px', display: 'block' }}>
                Play Store URL
              </label>
              <input
                type="url"
                className="ct-minimal-input"
                value={form.play_store_url}
                onChange={e => update('play_store_url', e.target.value)}
                placeholder="https://play.google.com/store/apps/details?id=..."
              />
            </div>

            {/* OR Divider */}
            <div className="ct-or-divider"><span>OR</span></div>

            {/* Option 3: Web App Link */}
            <div className="form-group" style={{ marginBottom: '0' }}>
              <label style={{ fontSize: '11px', fontWeight: 500, color: '#9ca3af', marginBottom: '6px', display: 'block' }}>
                Web App Link
              </label>
              <input
                type="url"
                className="ct-minimal-input"
                value={form.web_app_url}
                onChange={e => update('web_app_url', e.target.value)}
                placeholder="https://yourwebsite.com"
              />
            </div>

            <div className="ct-launch-checklist">
              <div className={`checklist-item ${form.app_name.trim() ? 'complete' : ''}`}>
                <div className="dot-indicator" />
                <span>App name</span>
              </div>
              <div className={`checklist-item ${form.test_instructions.trim() ? 'complete' : ''}`}>
                <div className="dot-indicator" />
                <span>Test instructions</span>
              </div>
              <div className={`checklist-item ${hasAppSource ? 'complete' : ''}`}>
                <div className="dot-indicator" />
                <span>App source provided</span>
              </div>
            </div>

            <button
              className="minimal-submit-btn razorpay-btn"
              disabled={uploading || !form.app_name.trim() || !form.test_instructions.trim() || !hasAppSource}
              onClick={() => handleSubmit('now')}
            >
              {uploading ? 'Launching...' : `Pay & Launch — ₹${PLANS.find(p => p.id === selectedPlan).price.toLocaleString()}`}
            </button>
            <button
              className="minimal-submit-btn-ghost"
              disabled={uploading || !form.app_name.trim() || !form.test_instructions.trim() || !hasAppSource}
              onClick={() => handleSubmit('later')}
            >
              Launch Free — Pay After Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
